import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import MedicationIcon from '@mui/icons-material/Medication';
import Toast, { useToast } from '../../components/useToast';
import { axiosErrorToUserMessage, axiosIntakeInstance } from '../../services/axios';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

export default function DownloadMedicationPdfButton({
  mkey,
  activeSiteId,
  patientID,
  onSuccess,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const intakeAxiosWithAuth = useAxiosPrivate(axiosIntakeInstance);
  const {
    toastState,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  const downloadMedicationPdf = async () => {
    try {
      setIsDownloading(true);

      if (!mkey || !activeSiteId) {
        toastSetErrorNotification('Missing required information to generate medication PDF');
        setIsDownloading(false);
        return;
      }

      const response = await intakeAxiosWithAuth.get(`/${mkey}/medicationPDF`, {
        responseType: 'json',
      });

      if (!response.data) {
        toastSetErrorNotification('Failed to generate medication PDFs');
        setIsDownloading(false);
        return;
      }

      const { pmrForm, pharmWorksheetForm } = response.data;

      if (!pmrForm && !pharmWorksheetForm) {
        toastSetErrorNotification('Failed to generate medication PDFs');
        setIsDownloading(false);
        return;
      }

      const downloadPdf = async (base64Data, filename) => {
        const dataUrl = `data:application/pdf;base64,${base64Data}`;
        const blob = await fetch(dataUrl).then((res) => res.blob());
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      };

      const downloadedForms = [];

      if (pmrForm) {
        await downloadPdf(
          pmrForm,
          `FirstHx_PersonalMedicationRecord_${patientID}.pdf`,
        );
        downloadedForms.push('Personal Medication Record');
      }

      if (pharmWorksheetForm) {
        await downloadPdf(
          pharmWorksheetForm,
          `FirstHx_PharmacistsWorksheet_${patientID}.pdf`,
        );
        downloadedForms.push('Pharmacist\'s Worksheet');
      }

      if (onSuccess) {
        const message = downloadedForms.length === 2
          ? 'Medication forms downloaded as PDFs'
          : `${downloadedForms[0]} downloaded as PDF`;
        onSuccess(message);
      }
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error) || 'An error occurred when downloading the medication PDFs.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Tooltip title="Download Medication Forms as PDFs" arrow>
        <IconButton
          onClick={downloadMedicationPdf}
          disabled={isDownloading}
          data-testid="downloadMedicationPdfIcon"
        >
          <MedicationIcon className="table-tool-icon" />
        </IconButton>
      </Tooltip>
      <Toast
        open={toastState.open}
        severity={toastState.severity}
        text={toastState.text}
        onClose={toastOnClose}
      />
    </>
  );
}

DownloadMedicationPdfButton.propTypes = {
  mkey: PropTypes.string.isRequired,
  activeSiteId: PropTypes.string.isRequired,
  patientID: PropTypes.string,
  onSuccess: PropTypes.func,
};

DownloadMedicationPdfButton.defaultProps = {
  patientID: '',
  onSuccess: null,
};
